import type { ReleasesDto } from '@nsb/core';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { api } from '../api.ts';
import { LoadError, Loading } from '../components/ui.tsx';
import { PublicPage } from './Public.tsx';
import { dateWords } from '../days.ts';

/**
 * What's new: the development blog (Jason, 2026-10-03). One article per
 * release that reached production, written when the release is shipped and
 * carried with it (api domain/releases.ts): a plain-language summary first,
 * then what was added, then what was fixed. Public, linked from the sign-in
 * page.
 */

const day = (iso: string) => dateWords(iso, 'long');

export function Blog() {
  return (
    <PublicPage title="What’s new" lede="The portal grows with every release. Each one, in plain words: what changed, what was added, and what was fixed.">
      <Articles />
    </PublicPage>
  );
}

/**
 * The releases, newest first, as the public blog and Settings → Development
 * tools both show them. They are kept by production, where each was recorded
 * as it shipped; a development build reads production's (apps/web/dev-proxy.ts).
 */
export function Articles() {
  const { data, error } = useQuery({ queryKey: ['releases'], queryFn: () => api<ReleasesDto>('/releases'), staleTime: 60_000 });
  return (
    <>
      {error ? <LoadError error={error} /> : null}
      {!data && !error ? <Loading /> : null}
      {data && !data.releases.length ? <p className="mt-10 text-ink-2">The first notes are on their way.</p> : null}
      {data?.releases.map((r) => (
        <article key={r.id} className="mt-12 border-t border-rule pt-8">
          <div className="label">{day(r.releasedAt)}</div>
          <h2 className="mt-2 text-[1.75rem] leading-tight">{r.title}</h2>
          <p className="mt-4 text-[1.0625rem] leading-relaxed">{r.summary}</p>
          {r.features.length ? (
            <section className="mt-7">
              <h3 className="label mb-3">Added</h3>
              <ul className="space-y-3">
                {r.features.map((f, i) => (
                  <li key={i} className="leading-relaxed">
                    <span className="font-medium">{f.title}.</span> <span className="text-ink-2">{f.detail}</span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
          {r.fixes.length ? (
            <section className="mt-7">
              <h3 className="label mb-3">Fixed</h3>
              <ul className="space-y-3">
                {r.fixes.map((f, i) => (
                  <li key={i} className="leading-relaxed">
                    <span className="font-medium">{f.title}.</span> <span className="text-ink-2">{f.detail}</span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </article>
      ))}
    </>
  );
}

/** The latest release, one line, to the blog: at the foot of every page of the portal (components/PortalFoot.tsx, design study 30). */
export function WhatsNew({ className = '' }: { className?: string }) {
  const { data } = useQuery({ queryKey: ['releases'], queryFn: () => api<ReleasesDto>('/releases'), staleTime: 60_000 });
  const latest = data?.releases[0];
  return (
    <p className={className}>
      <span className="label mr-2">What’s new</span>
      <Link to="/blog" className="link">
        {latest ? `${dateWords(latest.releasedAt, 'dayOrDate')}: ${latest.title}` : 'Notes on each release'}
      </Link>
    </p>
  );
}
